import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { X, Phone, Mail, Calendar, FileText, CheckSquare, Clock } from "lucide-react";
import UiText from "./UiText";

const ACTIVITY_TYPES = [
  { value: "call", label: "Call", icon: Phone, color: "#10b981" },
  { value: "email", label: "Email", icon: Mail, color: "#3b82f6" },
  { value: "meeting", label: "Meeting", icon: Calendar, color: "#8b5cf6" },
  { value: "note", label: "Note", icon: FileText, color: "#f59e0b" },
  { value: "task", label: "Task", icon: CheckSquare, color: "#ef4444" },
];

// Zod validation schema
const activitySchema = z.object({
  type: z.string()
    .min(1, "Activity type is required"),
  subject: z.string()
    .min(1, "Subject is required")
    .min(3, "Subject must be at least 3 characters")
    .max(200, "Subject cannot exceed 200 characters"),
  description: z.string()
    .min(1, "Description is required")
    .min(10, "Description must be at least 10 characters")
    .max(1000, "Description cannot exceed 1000 characters"),
  date: z.string()
    .min(1, "Date and time are required"),
  duration: z.string()
    .refine((val) => !val || (!isNaN(parseFloat(val)) && parseFloat(val) >= 0), "Duration must be a positive number")
    .optional()
    .or(z.literal("")),
  outcome: z.string()
    .optional()
    .or(z.literal("")),
});

export default function AddActivityModal({ isOpen, onClose, onSubmit, companyName, customerName, activity }) {
  const [apiError, setApiError] = useState("");
  const isEditMode = Boolean(activity);

  const getDefaultValues = (currentActivity) => ({
    type: currentActivity?.type || "call",
    subject: currentActivity?.subject || "",
    description: currentActivity?.description || "",
    date: currentActivity?.date
      ? new Date(currentActivity.date).toISOString().slice(0, 16)
      : new Date().toISOString().slice(0, 16),
    duration: currentActivity?.duration ? String(currentActivity.duration) : "",
    outcome: currentActivity?.outcome || "",
  });

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    watch,
    reset,
    setValue,
  } = useForm({
    resolver: zodResolver(activitySchema),
    mode: "onChange",
    defaultValues: getDefaultValues(activity),
  });

  const formValues = watch();

  const handleClose = () => {
    if (!isSubmitting) {
      setApiError("");
      reset(getDefaultValues(activity));
      onClose();
    }
  };

  React.useEffect(() => {
    if (isOpen) {
      reset(getDefaultValues(activity));
    }
  }, [activity, isOpen, reset]);

  const onFormSubmit = async (data) => {
    setApiError("");
    try {
      console.log("Form submitted with data:", data);
      await onSubmit(data);
      reset();
      onClose();
    } catch (error) {
      console.error("Form submission error:", error);
      const errorMessage = error.message || "Failed to save activity. Please try again.";
      setApiError(errorMessage);
    }
  };

  if (!isOpen) return null;

  const selectedType = ACTIVITY_TYPES.find((t) => t.value === formValues.type);
  const TypeIcon = selectedType?.icon || FileText;

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
        padding: "1rem",
      }}
      onClick={handleClose}
    >
      <div
        style={{
          backgroundColor: "white",
          borderRadius: "16px",
          width: "100%",
          maxWidth: "600px",
          maxHeight: "90vh",
          overflow: "auto",
          boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            padding: "1.5rem",
            borderBottom: "1px solid #e5e7eb",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            position: "sticky",
            top: 0,
            backgroundColor: "white",
            zIndex: 10,
          }}
        >
          <div>
            <UiText variant="title" as="h2" style={{ marginBottom: "0.25rem" }}>
              {isEditMode ? "Update Activity" : "Log Activity"}
            </UiText>
            <UiText variant="small" as="p" style={{ color: "#6b7280" }}>
              {companyName || customerName}
            </UiText>
          </div>
          <button
            onClick={handleClose}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              padding: "0.5rem",
              borderRadius: "8px",
              transition: "background-color 0.2s",
            }}
            onMouseOver={(e) => (e.currentTarget.style.backgroundColor = "#f3f4f6")}
            onMouseOut={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
          >
            <X size={20} color="#6b7280" />
          </button>
        </div>

        {/* API Error */}
        {apiError && (
          <div style={{
            padding: "1rem 1.5rem",
            backgroundColor: "#fef2f2",
            borderBottom: "1px solid #fecaca",
          }}>
            <UiText variant="small" as="p" style={{ color: "#dc2626" }}>
              {apiError}
            </UiText>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit(onFormSubmit)} style={{ padding: "1.5rem" }}>
          {/* Activity Type Selection */}
          <div style={{ marginBottom: "1.5rem" }}>
            <UiText variant="label" as="label" style={{ display: "block", marginBottom: "0.5rem", fontWeight: "600" }}>
              Activity Type
            </UiText>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: "0.5rem" }}>
              {ACTIVITY_TYPES.map((type) => {
                const Icon = type.icon;
                const isSelected = formValues.type === type.value;
                const isDisabled = isEditMode && !isSelected;
                return (
                  <button
                    key={type.value}
                    type="button"
                    onClick={() => {
                      if (!isEditMode) {
                        setValue("type", type.value);
                      }
                    }}
                    disabled={isEditMode}
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: "0.5rem",
                      padding: "0.75rem 0.5rem",
                      border: `2px solid ${isSelected ? type.color : "#e5e7eb"}`,
                      borderRadius: "12px",
                      backgroundColor: isSelected ? `${type.color}10` : "white",
                      cursor: isEditMode ? "not-allowed" : "pointer",
                      opacity: isDisabled ? 0.45 : 1,
                      transition: "all 0.2s",
                    }}
                    onMouseOver={(e) => {
                      if (!isSelected && !isEditMode) {
                        e.currentTarget.style.backgroundColor = "#f9fafb";
                        e.currentTarget.style.borderColor = type.color;
                      }
                    }}
                    onMouseOut={(e) => {
                      if (!isSelected && !isEditMode) {
                        e.currentTarget.style.backgroundColor = "white";
                        e.currentTarget.style.borderColor = "#e5e7eb";
                      }
                    }}
                  >
                    <Icon size={20} color={isSelected ? type.color : "#6b7280"} />
                    <span style={{ fontSize: "0.75rem", fontWeight: "500", color: isSelected ? type.color : "#6b7280" }}>
                      {type.label}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Subject */}
          <div style={{ marginBottom: "1.5rem" }}>
            <UiText variant="label" as="label" style={{ display: "block", marginBottom: "0.5rem", fontWeight: "600" }}>
              Subject <span style={{ color: "#ef4444" }}>*</span>
            </UiText>
            <input
              type="text"
              {...register("subject")}
              placeholder={`Enter ${selectedType?.label.toLowerCase()} subject`}
              style={{
                width: "100%",
                padding: "0.75rem",
                border: `1px solid ${errors.subject ? "#ef4444" : "#d1d5db"}`,
                borderRadius: "8px",
                fontSize: "0.875rem",
                outline: "none",
                transition: "border-color 0.2s",
              }}
              onFocus={(e) => !errors.subject && (e.target.style.borderColor = selectedType?.color || "#3b82f6")}
              onBlur={(e) => !errors.subject && (e.target.style.borderColor = "#d1d5db")}
            />
            {errors.subject && (
              <UiText variant="small" as="p" style={{ color: "#ef4444", marginTop: "0.25rem" }}>
                {errors.subject.message}
              </UiText>
            )}
          </div>

          {/* Date and Duration Row */}
          <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: "1rem", marginBottom: "1.5rem" }}>
            {/* Date & Time */}
            <div>
              <UiText variant="label" as="label" style={{ display: "block", marginBottom: "0.5rem", fontWeight: "600" }}>
                Date & Time <span style={{ color: "#ef4444" }}>*</span>
              </UiText>
              <div style={{ position: "relative" }}>
                <Clock size={18} color="#6b7280" style={{ position: "absolute", left: "0.75rem", top: "50%", transform: "translateY(-50%)" }} />
                <input
                  type="datetime-local"
                  {...register("date")}
                  style={{
                    width: "100%",
                    padding: "0.75rem 0.75rem 0.75rem 2.5rem",
                    border: `1px solid ${errors.date ? "#ef4444" : "#d1d5db"}`,
                    borderRadius: "8px",
                    fontSize: "0.875rem",
                    outline: "none",
                  }}
                />
              </div>
              {errors.date && (
                <UiText variant="small" as="p" style={{ color: "#ef4444", marginTop: "0.25rem" }}>
                  {errors.date.message}
                </UiText>
              )}
            </div>

            {/* Duration */}
            <div>
              <UiText variant="label" as="label" style={{ display: "block", marginBottom: "0.5rem", fontWeight: "600" }}>
                Duration (min)
              </UiText>
              <input
                type="number"
                {...register("duration")}
                placeholder="30"
                min="0"
                style={{
                  width: "100%",
                  padding: "0.75rem",
                  border: `1px solid ${errors.duration ? "#ef4444" : "#d1d5db"}`,
                  borderRadius: "8px",
                  fontSize: "0.875rem",
                  outline: "none",
                }}
              />
              {errors.duration && (
                <UiText variant="small" as="p" style={{ color: "#ef4444", marginTop: "0.25rem" }}>
                  {errors.duration.message}
                </UiText>
              )}
            </div>
          </div>

          {/* Description */}
          <div style={{ marginBottom: "1.5rem" }}>
            <UiText variant="label" as="label" style={{ display: "block", marginBottom: "0.5rem", fontWeight: "600" }}>
              Description <span style={{ color: "#ef4444" }}>*</span>
            </UiText>
            <textarea
              {...register("description")}
              placeholder="Add details about this activity..."
              rows={4}
              style={{
                width: "100%",
                padding: "0.75rem",
                border: `1px solid ${errors.description ? "#ef4444" : "#d1d5db"}`,
                borderRadius: "8px",
                fontSize: "0.875rem",
                outline: "none",
                resize: "vertical",
                fontFamily: "inherit",
              }}
              onFocus={(e) => !errors.description && (e.target.style.borderColor = selectedType?.color || "#3b82f6")}
              onBlur={(e) => !errors.description && (e.target.style.borderColor = "#d1d5db")}
            />
            {errors.description && (
              <UiText variant="small" as="p" style={{ color: "#ef4444", marginTop: "0.25rem" }}>
                {errors.description.message}
              </UiText>
            )}
          </div>

          {/* Outcome */}
          <div style={{ marginBottom: "1.5rem" }}>
            <UiText variant="label" as="label" style={{ display: "block", marginBottom: "0.5rem", fontWeight: "600" }}>
              Outcome
            </UiText>
            <select
              {...register("outcome")}
              style={{
                width: "100%",
                padding: "0.75rem",
                border: "1px solid #d1d5db",
                borderRadius: "8px",
                fontSize: "0.875rem",
                outline: "none",
                backgroundColor: "white",
                cursor: "pointer",
              }}
            >
              <option value="">Select outcome (optional)</option>
              <option value="successful">Successful</option>
              <option value="follow_up_required">Follow-up Required</option>
              <option value="no_answer">No Answer</option>
              <option value="left_message">Left Message</option>
              <option value="not_interested">Not Interested</option>
              <option value="scheduled_meeting">Scheduled Meeting</option>
            </select>
          </div>

          {/* Action Buttons */}
          <div style={{ display: "flex", gap: "0.75rem", paddingTop: "1rem", borderTop: "1px solid #e5e7eb" }}>
            <button
              type="button"
              onClick={handleClose}
              disabled={isSubmitting}
              style={{
                flex: 1,
                padding: "0.75rem",
                border: "1px solid #d1d5db",
                borderRadius: "8px",
                backgroundColor: "white",
                color: "#374151",
                fontWeight: "600",
                cursor: isSubmitting ? "not-allowed" : "pointer",
                opacity: isSubmitting ? 0.5 : 1,
                transition: "all 0.2s",
              }}
              onMouseOver={(e) => !isSubmitting && (e.currentTarget.style.backgroundColor = "#f9fafb")}
              onMouseOut={(e) => !isSubmitting && (e.currentTarget.style.backgroundColor = "white")}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              style={{
                flex: 1,
                padding: "0.75rem",
                border: "none",
                borderRadius: "8px",
                background: `linear-gradient(135deg, ${selectedType?.color || "#3b82f6"} 0%, ${selectedType?.color || "#3b82f6"}dd 100%)`,
                color: "white",
                fontWeight: "600",
                cursor: isSubmitting ? "not-allowed" : "pointer",
                opacity: isSubmitting ? 0.7 : 1,
                transition: "all 0.2s",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "0.5rem",
              }}
            >
              <TypeIcon size={18} />
              {isSubmitting ? "Saving..." : isEditMode ? "Update Activity" : "Log Activity"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
